"use client";

import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/components/library/Icon";
import Input from "@/components/library/Input";
import ConcurrencyPresetPicker from "@/components/settings/ConcurrencyPresetPicker";
import {
  getAllowedPresets,
  MAX_CUSTOMER_CONCURRENCY,
  MIN_CUSTOMER_CONCURRENCY,
} from "@/constants/concurrency";
import { useOrg } from "@/hooks/useOrg";
import { useRestrictions } from "@/hooks/useRestrictions";
import { GAIL_API } from "@/utils/common/constants/routes";
import { OnboardingStepHeader } from "./OnboardingStepHeader";
import { StepFooter } from "./StepFooter";

interface CallConcurrencyStepProps {
  readonly onBack?: () => void;
  readonly onNext: () => void;
}

const RECOMMENDED_CONCURRENCY = 3;

export function CallConcurrencyStep({ onBack, onNext }: CallConcurrencyStepProps) {
  const { t } = useTranslation("onboarding");
  const { refreshOrg } = useOrg();
  const { restrictions, isDefault: isRestrictionsDefault } = useRestrictions();

  const maxAllowed =
    restrictions.maxAllowedConcurrency ?? MAX_CUSTOMER_CONCURRENCY;
  const allowedPresets = useMemo(
    () => getAllowedPresets(maxAllowed),
    [maxAllowed]
  );
  const recommendedConcurrency = Math.min(RECOMMENDED_CONCURRENCY, maxAllowed);

  // Store as string so the input can be empty while editing
  const [concurrencyInput, setConcurrencyInput] = useState<string>(
    String(recommendedConcurrency)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // True when the org's current value is support-managed (above the customer
  // range). We never overwrite it unless the user picks a value.
  const [isSupportManaged, setIsSupportManaged] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  // Custom input stays collapsed unless the saved value is not a preset
  const [showCustom, setShowCustom] = useState(false);

  // Parsed numeric value (NaN when empty or invalid)
  const concurrencyValue = Number.parseInt(concurrencyInput, 10);
  const isWaitingForRestrictions = isRestrictionsDefault;

  // Fetch current concurrency value once real restrictions are loaded
  useEffect(() => {
    if (isRestrictionsDefault) {
      return;
    }

    const fetchConcurrency = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${GAIL_API}/.internal/call_queue/concurrency`, {
          credentials: "include",
          method: "GET",
        });

        if (response.ok) {
          const data = await response.text();
          const rawValue = Number.parseInt(data, 10);
          if (!Number.isNaN(rawValue)) {
            // 0 is no longer allowed — surface as the org's max.
            const value = rawValue === 0 ? maxAllowed : rawValue;
            if (value >= MIN_CUSTOMER_CONCURRENCY && value <= maxAllowed) {
              setConcurrencyInput(String(value));
              if (!allowedPresets.includes(value)) {
                setShowCustom(true);
              }
            } else {
              setIsSupportManaged(true);
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch concurrency", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConcurrency();
  }, [allowedPresets, isRestrictionsDefault, maxAllowed]);

  // Clamp value on blur so the user can freely type, then we fix it when they leave
  const handleInputBlur = useCallback(() => {
    const parsed = Number.parseInt(concurrencyInput, 10);
    if (Number.isNaN(parsed) || parsed < MIN_CUSTOMER_CONCURRENCY) {
      setConcurrencyInput(String(recommendedConcurrency));
    } else if (parsed > maxAllowed) {
      setConcurrencyInput(String(maxAllowed));
    }
  }, [concurrencyInput, maxAllowed, recommendedConcurrency]);

  const handleSave = useCallback(async () => {
    setError(null);

    if (isRestrictionsDefault) {
      return;
    }

    // Don't silently downgrade a support-managed value when the user just
    // clicks Next without choosing anything.
    if (isSupportManaged && !hasInteracted) {
      onNext();
      return;
    }

    const value = Number.parseInt(concurrencyInput, 10);
    if (Number.isNaN(value) || value < MIN_CUSTOMER_CONCURRENCY || value > maxAllowed) {
      setError(t("callConcurrency.invalidValue", { max: maxAllowed }));
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${GAIL_API}/.internal/call_queue/concurrency`, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: value.toString(),
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to save concurrency");
      }

      // Re-fetch org data so the UI reflects the newly saved concurrency value
      await refreshOrg();
      onNext();
    } catch (err) {
      console.error("Failed to save call concurrency", err);
      setError(t("callConcurrency.error"));
    } finally {
      setIsSaving(false);
    }
  }, [
    concurrencyInput,
    maxAllowed,
    isRestrictionsDefault,
    isSupportManaged,
    hasInteracted,
    refreshOrg,
    onNext,
    t,
  ]);

  const presetOptions = [
    { value: 1, label: t("callConcurrency.presets.conservative") },
    { value: 3, label: t("callConcurrency.presets.moderate") },
    { value: 5, label: t("callConcurrency.presets.aggressive") },
  ].filter((option) => allowedPresets.includes(option.value));

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center">
        <div className="w-full max-w-lg space-y-8">
          {/* Header */}
          <OnboardingStepHeader
            title={t("callConcurrency.title")}
            description={t("callConcurrency.subtitle")}
          />

          {/* Icon */}
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Icon icon="phone" className="w-10 h-10 text-primary" />
            </div>
          </div>

          {isWaitingForRestrictions || isLoading ? (
            <div
              className="flex justify-center py-8"
              data-testid="call-concurrency-step-loading"
            >
              <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Preset buttons */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  {t("callConcurrency.presetsLabel")}
                </label>
                <ConcurrencyPresetPicker
                  compact
                  options={presetOptions}
                  selectedValue={concurrencyValue}
                  onSelect={(value) => {
                    setConcurrencyInput(String(value));
                    setHasInteracted(true);
                  }}
                />
              </div>

              {/* Custom input (collapsed behind a toggle) */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-200" />
                  <button
                    type="button"
                    onClick={() => setShowCustom(!showCustom)}
                    aria-expanded={showCustom}
                    className={clsx(
                      "flex items-center gap-1.5 text-xs uppercase tracking-wide",
                      "text-muted-foreground hover:text-foreground transition-colors"
                    )}
                  >
                    {t("callConcurrency.customDivider")}
                    <Icon
                      icon="chevron-down"
                      className={clsx(
                        "w-3 h-3 transition-transform duration-200",
                        showCustom && "rotate-180"
                      )}
                    />
                  </button>
                  <div className="h-px flex-1 bg-200" />
                </div>

                {showCustom && (
                  <Input
                    label={t("callConcurrency.customLabel")}
                    type="number"
                    // Icon-less inputs paint their own opaque background over the
                    // wrapper border's antialiasing; keep it transparent.
                    className={clsx(
                      "[&>input]:!bg-transparent",
                      "dark:[&>input]:!bg-transparent"
                    )}
                    min={MIN_CUSTOMER_CONCURRENCY}
                    max={maxAllowed}
                    value={concurrencyInput}
                    onChange={(e) => {
                      setConcurrencyInput(e.target.value);
                      setHasInteracted(true);
                    }}
                    onBlur={handleInputBlur}
                    helperText={t("callConcurrency.maxAllowed", { max: maxAllowed })}
                  />
                )}
              </div>

              {error && (
                <p className="text-sm text-red-600 dark:text-red-500 text-center">{error}</p>
              )}

              {/* Info card */}
              <div
                className={clsx(
                  "rounded-xl border border-amber-500/30 bg-amber-500/5 p-4",
                  "flex items-start gap-3"
                )}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Icon icon="bolt" className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    {t("callConcurrency.tipTitle")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("callConcurrency.tipDescription")}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer Buttons */}
      <StepFooter
        onBack={onBack}
        onSkip={onNext}
        onNext={handleSave}
        isSaving={isSaving}
        backDisabled={isWaitingForRestrictions || isLoading}
        nextDisabled={isWaitingForRestrictions || isLoading}
      />
    </div>
  );
}
